import { StateAssert } from '@csp/csp-common-model';
import { isDefined } from '@csp/csp-common-util';
import { findLast, orderBy, uniqWith } from 'lodash';
import { ScheduleRequestRef } from '../../model/ScheduleRequestRef';
import { ScheduleVersionInfo } from '../../model/schedulingModels/ScheduleVersionInfo';
import { GenericRequest } from '../../model/schedulingModels/GenericRequest';
import { GenericVersion } from '../../model/schedulingModels/GenericVersion';
import { RequestVersionStatus, RequestVersionStatuses } from './RequestVersionStatus';

export type RequestWithVersionInfo<Request extends GenericRequest = GenericRequest> = Request & {
  versionStatus: RequestVersionStatus;
  versions: ScheduleVersionInfo[];
};

/**
 * A request can exist in multiple versions, but only one can be active at any given time.
 * This mapper takes the request reference and all versions and inverts the data structure
 * so that a request has a list of versions instead of the other way around.
 *
 * We pick the request that is most relevant in this point in time
 * (even if they _should_ be identical). We also map a status that describes if the request
 * currently is in a active, expired och future version.
 */
const from = <Request extends GenericRequest, Version extends GenericVersion<Request>>(
  requestRef: ScheduleRequestRef,
  versions: Version[],
): RequestWithVersionInfo<Request> => {
  const allRequestsWithVersionInfo = versions
    .map(version => {
      const matchingRequest = version.requests.find(request =>
        ScheduleRequestRef.matchesRequestRef(requestRef, request),
      );
      return matchingRequest
        ? {
            versionStatus: RequestVersionStatuses.from(version),
            versionInfo: ScheduleVersionInfo.fromVersion(version),
            request: matchingRequest,
          }
        : undefined;
    })
    .filter(isDefined);

  const allRequestsWithVersionInfoDesc = orderBy(
    allRequestsWithVersionInfo,
    ({ versionInfo }) => versionInfo.activeFrom.unixTimeMillis,
    'desc',
  );

  // Pick the most relevant request according to status
  const currentRequestAndStatus =
    allRequestsWithVersionInfoDesc.find(({ versionStatus }) => versionStatus === RequestVersionStatus.ACTIVE) ??
    allRequestsWithVersionInfoDesc.find(({ versionStatus }) => versionStatus === RequestVersionStatus.EXPIRED) ??
    findLast(allRequestsWithVersionInfoDesc, version => version.versionStatus === RequestVersionStatus.FUTURE) ??
    allRequestsWithVersionInfoDesc[0];

  StateAssert.isDefined(
    currentRequestAndStatus,
    `Must provide a requestRef that exists in the provided versions. ref: '${requestRef.scheduleCode}-${requestRef.requestCode}'`,
  );

  return {
    ...currentRequestAndStatus.request,
    versions: allRequestsWithVersionInfo.map(({ versionInfo }) => versionInfo),
    versionStatus: currentRequestAndStatus.versionStatus,
  };
};

const fromVersions = <Request extends GenericRequest, Version extends GenericVersion<Request>>(
  versions: Version[],
): RequestWithVersionInfo<Request>[] => {
  const allScheduleRequestRefs = versions.flatMap(version => ScheduleRequestRef.fromGenericRequests(version.requests));
  const uniqueScheduleRequestRefs = uniqWith(allScheduleRequestRefs, ScheduleRequestRef.isSameRequestRef);

  return uniqueScheduleRequestRefs.map(ref => from(ref, versions));
};

export const RequestWithVersionInfo = {
  fromVersions,
  from,
};
